import React, { useCallback, useEffect, useState } from 'react';
import { googleAdsApi, leadApi } from '../services/api';
import { GoogleCut, GoogleOutcome, GoogleReport, LeadRow } from '../types';
import { fmtDay, fmtDayTime, fmtINR, istToday } from '../lib';
import DateRangeBar from '../components/DateRangeBar';
import NumberStrip, { StripItem } from '../components/NumberStrip';
import StagePill from '../components/StagePill';
import StageChips, { Dash } from '../components/StageChips';
import {
  Section, DAYPARTS, Empty, Failed, LeadCell, Loading, Note, Out, PageFrame, picked, Pill, Strip, Tabs, Th,
  advisorOf, btn, btnTone, td, tdBig, tdOurs, tr, trClick,
} from '../components/PlatformBits';

/** Google is the one platform whose click we can name: every ad click carries
 *  a gclid, the site keeps it, and Google's click log resolves it to keyword,
 *  ad, device and place. marketing-service does that join; this page only
 *  renders it — every row already carries our landed / tapped / lead ids. */

const OCID = '8305597102';
const gads = (path: string, q = '') => `https://ads.google.com/aw/${path}?ocid=${OCID}${q}`;

const inr = (micros: number) => fmtINR(micros / 1e6);
const n = (v: number) => v.toLocaleString('en-IN');
const pct = (v: number | null) => (v == null ? null : `${Math.round(v * 100)}%`);
const CHANNEL: Record<string, string> = { SEARCH: 'Search', DEMAND_GEN: 'Demand Gen', PERFORMANCE_MAX: 'Performance Max', VIDEO: 'YouTube' };
const QUALITY: Record<string, string> = { BELOW_AVERAGE: 'below', AVERAGE: 'average', ABOVE_AVERAGE: 'above' };
const DEVICE: Record<string, string> = { MOBILE: 'Phone', DESKTOP: 'Desktop', TABLET: 'Tablet', CONNECTED_TV: 'TV', OTHER: 'Other' };

/** The selected row's first cell carries a forest rule on its left edge. */

const Ours: React.FC<{ o: GoogleOutcome; stages: (ids: string[]) => Record<string, number>; cost?: number }> = ({ o, stages, cost }) => (
  <>
    <td className={`${td} text-right`}>{o.landed ? n(o.landed) : <Dash />}</td>
    <td className={`${td} text-right`}>{o.tapped || <Dash />}</td>
    <td className={tdOurs}>{o.lead_ids.length || <Dash />}</td>
    <td className="px-3 py-3 text-right">{o.lead_ids.length ? <StageChips stages={stages(o.lead_ids)} /> : <Dash />}</td>
    {cost !== undefined && (
      <td className={`${td} text-right`}>{o.lead_ids.length && cost ? inr(cost / o.lead_ids.length) : <Dash />}</td>
    )}
  </>
);
const OursTh: React.FC<{ perLead?: boolean }> = ({ perLead }) => (
  <><Th ours>Landed</Th><Th ours>Tapped</Th><Th ours>New leads</Th><Th ours>Stage</Th>{perLead && <Th>₹ / lead</Th>}</>
);

const GooglePage: React.FC = () => {
  const [range, setRange] = useState({ from: istToday(), to: istToday() });
  const [report, setReport] = useState<GoogleReport | null>(null);
  const [leads, setLeads] = useState<LeadRow[] | null>(null);
  const [tab, setTab] = useState<'keywords' | 'waste' | 'ads'>('keywords');
  const [cut, setCut] = useState<'devices' | 'hours' | 'cities'>('devices');
  const [selected, setSelected] = useState<string | null>(null);  // a keyword's criterion_id
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setReport(null); setLeads(null); setError(null);
    googleAdsApi.report(range.from, range.to)
      .then((rep) => { setReport(rep); return leadApi.byIds(Object.keys(rep.leads)).then(setLeads); })
      .catch((err: any) => setError(err?.response?.data?.detail || err.message));
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const refresh = () => {
    setRefreshing(true);
    googleAdsApi.refresh()
      .then(load)
      .catch((err: any) => setError(`Refresh failed: ${err?.response?.data?.detail || err.message}`))
      .finally(() => setRefreshing(false));
  };

  if (error) return <Failed what="Google" error={error} />;

  const leadById = new Map((leads ?? []).map((l) => [l.id, l]));
  const stagesOf = (ids: string[]): Record<string, number> => {
    const out: Record<string, number> = {};
    ids.forEach((id) => { const st = leadById.get(id)?.stage ?? 'new'; out[st] = (out[st] ?? 0) + 1; });
    return out;
  };

  const camps = report?.campaigns ?? [];
  const tot = camps.reduce((a, c) => ({
    spend: a.spend + c.cost_micros, clicks: a.clicks + c.clicks, landed: a.landed + c.landed, tapped: a.tapped + c.tapped,
    leads: a.leads.concat(c.lead_ids),
  }), { spend: 0, clicks: 0, landed: 0, tapped: 0, leads: [] as string[] });
  const stagesAll = stagesOf(tot.leads);
  // Spend on searches a lead was never going to come from: terms whose matched
  // keyword brought no lead in the window, plus our own name.
  const keywordLeads = new Map((report?.keywords ?? []).map((k) => [k.criterion_id, k.lead_ids.length]));
  const ourName = (t: string) => /bhuvana/.test(t);
  const waste = (report?.search_terms ?? []).filter((t) => t.cost_micros > 0 && (ourName(t.search_term) || !keywordLeads.get(t.criterion_id)));
  const wasteSpend = waste.reduce((a, t) => a + t.cost_micros, 0);

  const strip: StripItem[] = [
    { v: stagesAll.visited ?? 0, k: 'site visits', cls: 'visited' },
    { v: stagesAll.visit_scheduled ?? 0, k: 'scheduled', cls: 'gold' },
    { v: tot.leads.length, k: 'new leads' },
    { v: n(tot.landed), k: 'landed', p: `of ${n(tot.clicks)} clicks` },
    { v: inr(tot.spend), k: 'spend' },
    ...(tot.leads.length ? [{ v: inr(tot.spend / tot.leads.length), k: 'per lead' }] : []),
  ];

  const selectedKw = report?.keywords.find((k) => k.criterion_id === selected);
  const leadRows = (leads ?? []).filter((l) => !selectedKw || selectedKw.lead_ids.includes(l.id));
  const termsFor = (criterion: string | null, day: string) =>
    (report?.search_terms ?? []).filter((t) => t.criterion_id === criterion && t.days.includes(day) && t.clicks > 0).map((t) => t.search_term);

  const cutRows: { label: string; cost: number; clicks: number; o: GoogleOutcome }[] = !report ? [] :
    cut === 'hours'
      ? DAYPARTS.map((d) => {
        const rows = report.hours.filter((h) => d.has(h.hour!));
        return { label: d.label, cost: rows.reduce((a, r) => a + r.cost_micros, 0), clicks: rows.reduce((a, r) => a + r.clicks, 0),
          o: { landed: rows.reduce((a, r) => a + r.landed, 0), tapped: rows.reduce((a, r) => a + r.tapped, 0), lead_ids: rows.flatMap((r) => r.lead_ids) } };
      })
      : (report[cut] as GoogleCut[]).map((r) => ({
        label: cut === 'devices' ? (DEVICE[r.device!] ?? r.device!) : (r.city ? `${r.city}${r.region && r.region !== r.city ? ` · ${r.region}` : ''}` : 'Place unknown'),
        cost: r.cost_micros, clicks: r.clicks, o: r,
      }));

  return (
    <PageFrame
      title="Google"
      badge={{ platform: 'google' }}
      subline={<>
        Bhuvana Fortune Greens · 735-215-2346 · {report?.synced_at ? <>synced <b className="text-charcoal">{fmtDayTime(report.synced_at)}</b></> : 'sync time unknown'}
        {' · '}<Out href={gads('campaigns')}>Open Google Ads</Out>
      </>}
      action={<button onClick={refresh} disabled={refreshing || !report} className={`${btn} ${btnTone.forest}`}>{refreshing ? 'Refreshing…' : 'Refresh from Google'}</button>}
      bar={<DateRangeBar from={range.from} to={range.to} onChange={(from, to) => setRange({ from, to })} />}
    >
      {!report && <Loading what="Google" />}

      {report && (
        <>
          <Strip><NumberStrip items={strip} /></Strip>

          <Section title="Campaigns">
            <table className="w-full min-w-[980px] border-collapse">
              <thead><tr>
                <Th left>Campaign</Th><Th>Spend</Th><Th>Clicks</Th><Th>Impr. share</Th><OursTh perLead />
              </tr></thead>
              <tbody>
                {camps.length === 0 && <Empty cols={9}>No Google spend in this window.</Empty>}
                {camps.map((c) => (
                  <tr key={c.campaign_id} className={tr}>
                    <td className="px-3 py-3">
                      <a href={gads('adgroups', `&campaignId=${c.campaign_id}`)} target="_blank" rel="noreferrer" title={`${c.name} — open in Google Ads`}
                         className="block font-semibold text-[13.5px] leading-snug truncate max-w-[340px] text-charcoal hover:text-forest hover:underline underline-offset-2">{c.name} <span className="text-graphite">↗</span></a>
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap text-[11px] text-graphite">
                        <Pill tone={c.status === 'ENABLED' ? 'ok' : undefined}>{c.status === 'ENABLED' ? 'running' : (c.status ?? '').toLowerCase()}</Pill>
                        <Pill>{CHANNEL[c.channel_type ?? ''] ?? c.channel_type}</Pill>
                        {c.daily_budget_micros != null && <span>{inr(c.daily_budget_micros)} / day</span>}
                        {c.lost_to_rank != null && <span>misses <b className="text-brick">{pct(c.lost_to_rank)} on ad rank</b>, {pct(c.lost_to_budget)} on budget</span>}
                      </div>
                    </td>
                    <td className={tdBig}>{inr(c.cost_micros)}<div className="text-[11px] text-graphite font-normal">{n(c.impressions)} impressions</div></td>
                    <td className={`${td} text-right`}>{c.clicks ? <>{n(c.clicks)}<div className="text-[11px] text-graphite">{inr(c.cost_micros / c.clicks)} each</div></> : <Dash />}</td>
                    <td className={`${td} text-right`}>{pct(c.impression_share) ?? <Dash />}</td>
                    <Ours o={c} stages={stagesOf} cost={c.cost_micros} />
                  </tr>
                ))}
              </tbody>
            </table>
            <Note>
              Green columns are joined click by click: {n(report.arrivals - report.unresolved_arrivals)} of {n(report.arrivals)} arrivals with a Google click id resolved to a click.
              {' '}<b className="text-charcoal">Impr. share</b> — share of eligible searches our ad showed on (Search only).
            </Note>
          </Section>

          <Section title="Searches" note={<span className="inline-flex items-center gap-3 flex-wrap">
            {selectedKw && <span>leads below are from <b className="text-charcoal">{selectedKw.keyword}</b> · <button className="font-semibold text-forest hover:underline" onClick={() => setSelected(null)}>show all</button></span>}
            <Tabs value={tab} onChange={setTab} items={[['keywords', 'Keywords'], ['waste', 'No lead'], ['ads', 'Ads']]} />
          </span>}>
            {tab === 'keywords' && (
              <table className="w-full min-w-[980px] border-collapse">
                <thead><tr>
                  <Th left>Keyword</Th><Th left>Quality</Th><Th>Impr. share</Th><Th>Clicks</Th><Th>Spend</Th><OursTh perLead />
                </tr></thead>
                <tbody>
                  {report.keywords.length === 0 && <Empty cols={10}>No keyword ran in this window (Search campaigns only).</Empty>}
                  {report.keywords.map((k) => (
                    <tr key={k.criterion_id} onClick={() => setSelected(selected === k.criterion_id ? null : k.criterion_id)} className={trClick(selected === k.criterion_id)}>
                      <td className={`px-3 py-3 ${picked(selected === k.criterion_id)}`}>
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold text-[13.5px] truncate max-w-[280px]" title={k.keyword}>{k.keyword}</span>
                          <Pill>{(k.match_type ?? '').toLowerCase()}</Pill>
                        </div>
                        <div className="text-[11px] text-graphite mt-0.5 truncate max-w-[320px]" title={k.ad_group ?? undefined}>{k.ad_group}</div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        {k.quality_score != null
                          ? <><span className={`num font-semibold ${k.quality_score <= 3 ? 'text-brick' : ''}`}>{k.quality_score}/10</span>{' '}
                              {[k.expected_ctr, k.ad_relevance, k.landing_page].map((q, i) => (
                                <Pill key={i} tone={q === 'BELOW_AVERAGE' ? 'warn' : q === 'ABOVE_AVERAGE' ? 'ok' : undefined}>{QUALITY[q ?? ''] ?? '—'}</Pill>
                              ))}</>
                          : <span className="text-graphite">too few searches to score</span>}
                      </td>
                      <td className={`${td} text-right`}>{pct(k.impression_share) ?? <Dash />}</td>
                      <td className={`${td} text-right`}>{n(k.clicks)}</td>
                      <td className={tdBig}>{inr(k.cost_micros)}</td>
                      <Ours o={k} stages={stagesOf} cost={k.cost_micros} />
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {tab === 'waste' && (
              <table className="w-full min-w-[860px] border-collapse">
                <thead><tr><Th left>Search typed</Th><Th left>Matched keyword</Th><Th>Clicks</Th><Th>Spend</Th><Th>Google conversions</Th><Th left>Why</Th></tr></thead>
                <tbody>
                  {waste.length === 0 && <Empty cols={6}>Every paid search matched a keyword that brought a lead.</Empty>}
                  {waste.map((t) => (
                    <tr key={`${t.search_term}|${t.criterion_id}`} className={tr}>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold truncate max-w-[300px]" title={t.search_term}>{t.search_term}</span>
                          {(t.status === 'EXCLUDED' || t.status === 'ADDED_EXCLUDED') && <Pill tone="ok">already excluded</Pill>}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-graphite"><span className="block truncate max-w-[240px]" title={t.keyword ?? undefined}>{t.keyword}</span></td>
                      <td className={`${td} text-right`}>{n(t.clicks)}</td>
                      <td className={tdBig}>{inr(t.cost_micros)}</td>
                      <td className={`${td} text-right`}>{t.conversions || <Dash />}</td>
                      <td className="px-3 py-3 text-graphite text-[12.5px]">{ourName(t.search_term) ? 'our own name — they would have found us free' : 'keyword brought no lead in this window'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {tab === 'ads' && (
              <table className="w-full min-w-[900px] border-collapse">
                <thead><tr><Th left>Ad</Th><Th>Clicks</Th><Th>Spend</Th><Th>Click rate</Th><OursTh perLead /></tr></thead>
                <tbody>
                  {report.ads.map((a) => (
                    <tr key={a.ad_id} className={tr}>
                      <td className="px-3 py-3">
                        <div className="font-semibold text-[13.5px] leading-snug truncate max-w-[380px]" title={a.headlines.join(' · ')}>{a.headlines.slice(0, 3).map((h) => `“${h}”`).join(' · ') || a.ad_type}</div>
                        <div className="flex items-center gap-1.5 mt-1 text-[11px] text-graphite">
                          {a.approval_status && <Pill tone={/APPROVED/.test(a.approval_status) ? 'ok' : /DISAPPROVED/.test(a.approval_status) ? 'warn' : undefined}>{a.approval_status.replace(/_/g, ' ').toLowerCase()}</Pill>}
                          <span className="truncate max-w-[300px]" title={`${a.ad_group} · ${(a.ad_type ?? '').replace(/_/g, ' ').toLowerCase()}`}>{a.ad_group} · {(a.ad_type ?? '').replace(/_/g, ' ').toLowerCase()}</span>
                        </div>
                      </td>
                      <td className={`${td} text-right`}>{n(a.clicks)}</td>
                      <td className={tdBig}>{inr(a.cost_micros)}</td>
                      <td className={`${td} text-right`}>{a.impressions ? `${(a.clicks / a.impressions * 100).toFixed(1)}%` : <Dash />}</td>
                      <Ours o={a} stages={stagesOf} cost={a.cost_micros} />
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <Note>
              {tab === 'keywords' && <>Click a keyword to see its leads. <Out href={gads('keywords')}>Edit keywords</Out></>}
              {tab === 'waste' && <>{wasteSpend ? <><b className="text-charcoal">{inr(wasteSpend)}</b> on searches that cannot become leads. </> : null}<Out href={gads('keywords/negative')}>Add negative keywords</Out></>}
              {tab === 'ads' && <Out href={gads('ads')}>Edit ads</Out>}
            </Note>
          </Section>

          <Section title="Where and when" note={<Tabs value={cut} onChange={setCut} items={[['devices', 'Device'], ['hours', 'Time of day'], ['cities', 'Place']]} />}>
            <table className="w-full min-w-[800px] border-collapse">
              <thead><tr><Th left>{cut === 'devices' ? 'Device' : cut === 'hours' ? 'Time of day (IST)' : 'Place'}</Th><Th>Spend</Th><Th>Clicks</Th><Th>₹ / click</Th><OursTh /></tr></thead>
              <tbody>
                {cutRows.map((r) => (
                  <tr key={r.label} className={tr}>
                    <td className="px-3 py-3 font-semibold whitespace-nowrap">{r.label}</td>
                    <td className={tdBig}>{inr(r.cost)}</td>
                    <td className={`${td} text-right`}>{n(r.clicks)}</td>
                    <td className={`${td} text-right`}>{r.clicks ? inr(r.cost / r.clicks) : <Dash />}</td>
                    <Ours o={r.o} stages={stagesOf} />
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          <Section title="Google's conversions" note="Google bids toward all of these equally">
            <table className="w-full min-w-[520px] border-collapse">
              <thead><tr><Th left>Conversion</Th><Th>Google says</Th></tr></thead>
              <tbody>
                {report.conversions.map((c) => (
                  <tr key={c.action_id} className={tr}>
                    <td className="px-3 py-3 font-semibold">{c.action_name}</td>
                    <td className={tdBig}>{n(c.conversions)}</td>
                  </tr>
                ))}
                <tr className="border-t border-charcoal/15"><td className="px-3 py-3 font-semibold text-forest">…and became a lead in the CRM</td><td className={tdOurs}>{tot.leads.length}</td></tr>
              </tbody>
            </table>
          </Section>

          <Section title="Dig deeper in Google Ads" pad>
            <div className="flex flex-wrap gap-x-5 gap-y-2 text-[12.5px]">
              <Out href={gads('insights/auctioninsights')}>Who we compete with</Out>
              <Out href={gads('keywords/searchterms')}>Every search term</Out>
              <Out href={gads('ads/assetdetails')}>Headline and description ratings</Out>
              <Out href={gads('audiences/demographics')}>Age and gender</Out>
              <Out href={gads('placements/wheretheyshowed')}>Where Demand Gen ads ran</Out>
              <Out href={gads('changehistory')}>Change history</Out>
              <Out href={gads('recommendations')}>Recommendations</Out>
            </div>
          </Section>
          <Section title={selectedKw ? `Leads from “${selectedKw.keyword}”` : 'Leads from Google'} note={leads ? `${leadRows.length} lead${leadRows.length === 1 ? '' : 's'}` : 'loading…'}>
            <table className="w-full min-w-[1040px] border-collapse">
              <thead><tr>
                <Th left>Lead</Th><Th left>Came in</Th><Th left>Keyword</Th><Th left>Searches that day</Th><Th left>Device · place</Th><Th left>Stage</Th><Th left>Advisor</Th>
              </tr></thead>
              <tbody>
                {leads && leadRows.length === 0 && <Empty cols={7}>No lead in this window came from a Google click.</Empty>}
                {leadRows.map((l) => {
                  const c = report.leads[l.id];
                  const camp = camps.find((x) => x.campaign_id === c?.campaign_id);
                  const terms = c ? termsFor(c.criterion_id, c.clicked_at.slice(0, 10)) : [];
                  return (
                    <tr key={l.id} className={tr}>
                      <LeadCell id={l.id} name={l.name} sub={l.mobile ?? ''} />
                      <td className="px-3 py-3 whitespace-nowrap text-graphite">{fmtDay(l.created_at)}</td>
                      <td className="px-3 py-3">
                        {c?.keyword ? <span className="font-semibold">{c.keyword}</span> : <span className="text-graphite">{camp ? `${CHANNEL[camp.channel_type ?? ''] ?? camp.channel_type} · no keyword` : 'click older than Google keeps'}</span>}
                        {camp && <div className="text-[11px] text-graphite truncate max-w-[260px]" title={camp.name}>{camp.name}</div>}
                      </td>
                      <td className="px-3 py-3 text-graphite text-[12.5px] max-w-[300px]">{terms.length ? terms.join(' · ') : <Dash />}</td>
                      <td className="px-3 py-3 whitespace-nowrap text-graphite">{c ? `${DEVICE[c.device ?? ''] ?? c.device ?? '—'} · ${c.city ?? 'place unknown'}` : <Dash />}</td>
                      <td className="px-3 py-3 whitespace-nowrap"><StagePill stage={l.stage} /></td>
                      <td className="px-3 py-3 whitespace-nowrap">{advisorOf(l) ?? <Dash />}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Note>Searches are the ones Google logged against that keyword that day — it does not say which one a single click typed.</Note>
          </Section>

        </>
      )}
    </PageFrame>
  );
};

export default GooglePage;
